import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  Wallet,
  CalendarRange,
  Banknote,
  Coffee,
  Users,
  FileBarChart2,
  Plus,
  Send,
  ArrowRight,
  BookOpen,
  Library,
} from "lucide-react";
import api from "@/lib/api";
import { formatRupeesCompact, INVESTMENT_TYPE_LABEL } from "@/lib/format";

const PAGES = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, hint: "Overview" },
  { to: "/monthly-income", label: "Monthly Income", icon: CalendarRange, hint: "Grid" },
  { to: "/cash-book", label: "Cash & Bank", icon: BookOpen, hint: "Money flow" },
  { to: "/investments", label: "Investments", icon: Wallet, hint: "Portfolio" },
  { to: "/loans", label: "Loan Tracker", icon: Banknote, hint: "Interest schedule" },
  { to: "/cafe", label: "Cafe P&L", icon: Coffee, hint: "Bean Board" },
  { to: "/partners", label: "Partners", icon: Users, hint: "Ownership" },
  { to: "/masters", label: "Masters", icon: Library, hint: "Vendors, customers, accounts, categories" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, hint: "PDF & Excel" },
];

const ACTIONS = [
  { to: "/investments/new", label: "Add investment", icon: Plus, hint: "New asset", roles: ["admin", "accounts"] },
  { to: "/cash-book", label: "Record cash / bank entry", icon: BookOpen, hint: "Money flow", roles: ["admin", "accounts"] },
  { to: "/partners", label: "Distribute profit", icon: Send, hint: "Split to partners", roles: ["admin"] },
];

export default function CommandPalette({ open, onOpenChange, userRole }) {
  const nav = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/investments").catch(() => ({ data: [] })),
      api.get("/partners").catch(() => ({ data: [] })),
    ]).then(([i, p]) => {
      setInvestments(i.data);
      setPartners(p.data);
    });
  }, [open]);

  const go = (to) => {
    onOpenChange(false);
    nav(to);
  };

  const allowed = (roles) => !roles || roles.includes(userRole);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Jump to a page, investment, partner…" data-testid="cmdk-input" />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>No matches.</CommandEmpty>

        <CommandGroup heading="Pages">
          {PAGES.map((p) => (
            <CommandItem
              key={p.to}
              value={`page ${p.label} ${p.hint}`}
              onSelect={() => go(p.to)}
              data-testid={`cmdk-page-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <p.icon className="mr-2 h-4 w-4 text-slate-500" strokeWidth={1.75} />
              <span>{p.label}</span>
              <span className="ml-auto text-[11px] text-slate-400">{p.hint}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {ACTIONS.some((a) => allowed(a.roles)) && (
          <CommandGroup heading="Actions">
            {ACTIONS.filter((a) => allowed(a.roles)).map((a) => (
              <CommandItem
                key={a.to + a.label}
                value={`action ${a.label} ${a.hint}`}
                onSelect={() => go(a.to)}
              >
                <a.icon className="mr-2 h-4 w-4 text-red-600" strokeWidth={1.75} />
                <span>{a.label}</span>
                <span className="ml-auto text-[11px] text-slate-400">{a.hint}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {investments.length > 0 && (
          <CommandGroup heading="Investments">
            {investments.map((inv) => (
              <CommandItem
                key={inv.id}
                value={`inv ${inv.name} ${INVESTMENT_TYPE_LABEL[inv.type] || ""}`}
                onSelect={() => go(`/investments/${inv.id}`)}
                data-testid={`cmdk-inv-${inv.id}`}
              >
                <ArrowRight className="mr-2 h-4 w-4 text-slate-400" strokeWidth={1.75} />
                <span className="truncate">{inv.name}</span>
                <span className="ml-auto text-[11px] text-slate-500 font-mono-fin tabular-nums">
                  {formatRupeesCompact(inv.current_value)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {partners.length > 0 && (
          <CommandGroup heading="Partners">
            {partners.map((p) => (
              <CommandItem
                key={p.id}
                value={`partner ${p.name} ${p.email || ""}`}
                onSelect={() => go(`/partners`)}
              >
                <div className="mr-2 h-5 w-5 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white text-[9px] flex items-center justify-center font-semibold">
                  {p.name.split(" ").slice(0, 2).map((n) => n[0]).join("")}
                </div>
                <span className="truncate">{p.name}</span>
                <span className="ml-auto text-[11px] text-slate-400 font-mono-fin">{p.ownership_percent}%</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
      <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-400">
        <div className="flex gap-3">
          <span><kbd className="font-mono-fin border border-slate-200 px-1 rounded">↑↓</kbd> Move</span>
          <span><kbd className="font-mono-fin border border-slate-200 px-1 rounded">↵</kbd> Open</span>
          <span><kbd className="font-mono-fin border border-slate-200 px-1 rounded">esc</kbd> Close</span>
        </div>
        <span className="tracking-[0.2em] uppercase">MARKSAM · v1.0</span>
      </div>
    </CommandDialog>
  );
}
